import {
  BookOpenText,
  Briefcase,
  ClockCounterClockwise,
  FunnelSimple,
  GraduationCap,
  ListStar,
  MapPin,
  Star,
  Tag,
  Translate,
  UserList,
} from "@phosphor-icons/react";

import type { Lead } from "common/types";

import DesktopLabels from "./components/DesktopLabels";
import LeadSource from "./components/LeadSource";
import LeadsMobileLabels from "./components/LeadsMobileList/MobileLabels";
import LeadCompanyTimeTag from "./components/LeadTags/LeadCompanyTimeTag";
import LeadDetailTag from "./components/LeadTags/LeadDetailTag";
import LinkedInAccountsTag from "./components/LinkedInAccountsTag";

import type { JSX } from "react";
import type { LeadsSearchParams } from "./types";

export const DEFAULT_LEADS_SEARCH_PARAMS: LeadsSearchParams<never> = {
  keywords: "",
  favorite: false,
  origins: [],
  past_companies: [],
  current_companies: [],
  locations: [],
  labels: [],
  interests: [],
  skills: [],
  education: [],
  languages: [],
  lead_of: [],
};

export const LEADS_FILTERS: {
  [K in keyof Omit<LeadsSearchParams<never>, "keywords">]: {
    icon: JSX.Element;
    label: string;
    isBoolean?: true;
  };
} = {
  favorite: {
    icon: <Star weight="fill" />,
    label: "Favorite",
    isBoolean: true,
  },
  lead_of: { icon: <UserList weight="fill" />, label: "Account" },
  origins: { icon: <FunnelSimple weight="fill" />, label: "Origin" },
  current_companies: {
    icon: <Briefcase weight="fill" />,
    label: "Current company",
  },
  past_companies: {
    icon: <ClockCounterClockwise />,
    label: "Past company",
  },
  locations: {
    icon: <MapPin weight="fill" />,
    label: "Location",
  },
  labels: {
    icon: <Tag weight="fill" />,
    label: "Label",
  },
  education: {
    icon: <GraduationCap weight="fill" />,
    label: "Education",
  },
  skills: {
    icon: <BookOpenText weight="fill" />,
    label: "Skill",
  },
  interests: { icon: <ListStar weight="fill" />, label: "Interest" },
  languages: { icon: <Translate weight="fill" />, label: "Language" },
};

export const LEADS_COLUMNS = {
  accounts: {
    label: "Accounts",
    icon: UserList,
    isDefault: true,
    renderTag: ({ lead_of }: Lead, isDesktop: boolean) => (
      <LinkedInAccountsTag
        accountIds={lead_of}
        size={isDesktop ? "md" : "sm"}
      />
    ),
  },
  origins: {
    label: "Origins",
    icon: FunnelSimple,
    renderTag: ({ id }: Pick<Lead, "id">, isDesktop: boolean) => (
      <LeadSource leadId={id} variant={isDesktop ? "plain" : "tagSm"} />
    ),
  },
  currentCompanies: {
    label: "Current company",
    icon: Briefcase,
    renderTag: ({ id }: Pick<Lead, "id">) => (
      <LeadDetailTag leadId={id} leadKey="current_experience" />
    ),
  },
  currentCompaniesTime: {
    label: "Current company time",
    icon: Briefcase,
    renderTag: ({ id }: Pick<Lead, "id">) => (
      <LeadCompanyTimeTag leadId={id} leadKey="current_experience" />
    ),
  },
  pastCompanies: {
    label: "Past company",
    icon: ClockCounterClockwise,
    renderTag: ({ id }: Pick<Lead, "id">) => (
      <LeadDetailTag leadId={id} leadKey="work_experience" />
    ),
  },
  pastCompaniesTime: {
    label: "Past company time",
    icon: ClockCounterClockwise,
    renderTag: ({ id }: Pick<Lead, "id">) => (
      <LeadCompanyTimeTag leadId={id} leadKey="work_experience" />
    ),
  },
  location: {
    label: "Location",
    icon: MapPin,
    renderTag: ({ id }: Pick<Lead, "id">) => (
      <LeadDetailTag leadId={id} leadKey="location" />
    ),
  },
  education: {
    label: "Education",
    icon: GraduationCap,
    renderTag: ({ id }: Pick<Lead, "id">) => (
      <LeadDetailTag leadId={id} leadKey="education" />
    ),
  },
  skills: {
    label: "Skills",
    icon: BookOpenText,
    renderTag: ({ id }: Pick<Lead, "id">) => (
      <LeadDetailTag leadId={id} leadKey="skills" />
    ),
  },
  interests: {
    label: "Interests",
    icon: ListStar,
    renderTag: ({ id }: Pick<Lead, "id">) => (
      <LeadDetailTag leadId={id} leadKey="interests" />
    ),
  },
  languages: {
    label: "Languages",
    icon: Translate,
    renderTag: ({ id }: Pick<Lead, "id">) => (
      <LeadDetailTag leadId={id} leadKey="languages" />
    ),
  },
  labels: {
    label: "Labels",
    icon: Tag,
    renderTag: ({ id, labels }: Lead, isDesktop: boolean) =>
      isDesktop ? (
        <DesktopLabels labels={labels} profileId={id} />
      ) : (
        <LeadsMobileLabels labels={labels} />
      ),
  },
} as const;
